import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Card,
  CardActionArea,
  CardContent,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "./MenuListModal.css";

const MenuListModal = ({ menus, onClose, onMenuSelect }) => {
  const theme = useTheme();

  const handleMenuClick = (menu) => {
    onMenuSelect(menu);
    // onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 24px 20px 24px",
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: theme.palette.primary.main,
              color: "white",
            }}
          >
            <MenuBookIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          >
            Available Menus
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            width: 36,
            height: 36,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
              transform: "rotate(90deg)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: { xs: "20px 16px", sm: "24px" },
          "&.MuiDialogContent-root": {
            paddingTop: { xs: "20px", sm: "24px" },
          },
        }}
      >
        {menus.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "60px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: theme.palette.action.hover,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <MenuBookIcon
                sx={{ fontSize: 40, color: theme.palette.text.disabled }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              No menus available at the moment
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {menus.map((menu, index) => (
              <Card
                key={menu.id || index}
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.divider}`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: "hidden",
                  opacity: 0,
                  animation: `slideIn 0.4s ease-out ${index * 0.08}s forwards`,
                  "@keyframes slideIn": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 20px ${theme.palette.primary.main}20`,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleMenuClick(menu)}
                  sx={{
                    padding: { xs: "16px", sm: "20px" },
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <CardContent sx={{ padding: "0 !important" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flex: 1,
                        }}
                      >
                        {/* <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 48,
                            height: 48,
                            borderRadius: "10px",
                            backgroundColor: theme.palette.primary.main + "15",
                            color: theme.palette.primary.main,
                            flexShrink: 0,
                          }}
                        >
                          <MenuBookIcon sx={{ fontSize: 24 }} />
                        </Box> */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              fontSize: { xs: "1rem", sm: "1.125rem" },
                              mb: 0.5,
                              lineHeight: 1.3,
                            }}
                          >
                            {menu.name || menu.title || menu.menu_name || "Menu"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: "0.75rem",
                              display: "block",
                            }}
                          >
                            Click to view menu
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: theme.palette.primary.main,
                          transition: "transform 0.2s ease",
                          ".MuiCardActionArea:hover &": {
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MenuListModal;

